import React, { useEffect, useState } from "react";
import { Season, User } from "../../types";
import { Button } from "../common/Button";
import {
  getListSeasons,
  getListSeasonEnrollments,
  createSeasonEnrollments,
  getListUsers,
  userLabel,
} from "../../api";

export const SeasonEnrollmentsPanel: React.FC = () => {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [currentSeasonId, setCurrentSeasonId] = useState<number | null>(null);
  const [subscribedUsers, setSubscribedUsers] = useState<User[]>([]);
  const [unsubscribedUsers, setUnsubscribedUsers] = useState<User[]>([]);
  const [message, setMessage] = useState("");
  
  // Carica le stagioni
  useEffect(() => {
    // Lista stagioni
    const loadData = async () => {
      const users = await getListUsers();
      setUsers(users);
      const seasons = await getListSeasons();
      setSeasons(seasons);
      setCurrentSeasonId(seasons.length > 0 ? seasons[0].id : null);
    }
    loadData();
  }, []);

  // Carica utenti iscritti e non iscritti per la stagione corrente
  useEffect(() => {
    if (!currentSeasonId) return;
    if (!users.length) return;

    const loadData = async () => {
      const data = await getListSeasonEnrollments(currentSeasonId);
      const dizionarioUsers: { [key: number]: User } = users.reduce((acc, u) => {
        acc[u.id] = u;
        return acc;
      }, {} as Record<number, User>);

      const subscribed = data.map((se) => dizionarioUsers[se.user]);
      setSubscribedUsers(subscribed);
      const subscribedIds = subscribed.map((u) => u.id);
      const unsubscribed = users.filter((u) => subscribedIds.indexOf(u.id) < 0);
      setUnsubscribedUsers(unsubscribed);
    }
    loadData();
  }, [currentSeasonId, users]);

  const toggleUser = (user: User, subscribed: boolean) => {
    if (subscribed) {
      // sposta da iscritti -> non iscritti
      setSubscribedUsers((prev) => prev.filter((u) => u.id !== user.id));
      setUnsubscribedUsers((prev) => [...prev, user]);
    } else {
      // sposta da non iscritti -> iscritti
      setUnsubscribedUsers((prev) => prev.filter((u) => u.id !== user.id));
      setSubscribedUsers((prev) => [...prev, user]);
    }
  };

  const handleSelectAll = (subscribed: boolean) => {
    if (subscribed) {
      setSubscribedUsers([...subscribedUsers, ...unsubscribedUsers]);
      setUnsubscribedUsers([]);
    } else {
      setUnsubscribedUsers([...unsubscribedUsers, ...subscribedUsers]);
      setSubscribedUsers([]);
    }
  };

  const handleSave = async () => {
    if (!currentSeasonId) return;
    await createSeasonEnrollments(currentSeasonId, {
      user_ids: subscribedUsers.map((u) => u.id),
    });
    setMessage("Iscrizioni salvate!");
  };

  return (
    <div className="space-y-6">
      {/* Selettore stagione */}
      <div>
        <label className="block text-sm font-medium mb-2">Seleziona stagione</label>
        <select
          className="border border-gray-300 rounded px-3 py-2 w-full"
          value={currentSeasonId ?? ""}
          onChange={(e) => setCurrentSeasonId(Number(e.target.value))}
        >
          {seasons.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      {message && <div className="text-green-600 text-sm mb-4">{message}</div>}

      {currentSeasonId && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Utenti iscritti */}
          <div className="bg-white shadow-md rounded p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Iscritti</h3>
              <Button variant="warning" onClick={() => handleSelectAll(false)}>
                Rimuovi tutti
              </Button>
            </div>
            <div className="space-y-2">
              {subscribedUsers.map((user) => (
                <label
                  key={user.id}
                  className="flex items-center space-x-2 p-2 border border-gray-300 rounded"
                >
                  <input
                    type="checkbox"
                    checked
                    onChange={() => toggleUser(user, true)}
                  />
                  <span>
                    {userLabel(user)}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Utenti non iscritti */}
          <div className="bg-white shadow-md rounded p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Non iscritti</h3>
              <Button variant="success" onClick={() => handleSelectAll(true)}>
                Iscrivi tutti
              </Button>
            </div>
            <div className="space-y-2">
              {unsubscribedUsers.map((user) => (
                <label
                  key={user.id}
                  className="flex items-center space-x-2 p-2 border border-gray-300 rounded"
                >
                  <input
                    type="checkbox"
                    checked={false}
                    onChange={() => toggleUser(user, false)}
                  />
                  <span>
                    {userLabel(user)}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {currentSeasonId && (
        <div className="flex justify-end">
          <Button onClick={handleSave} variant="primary">
            Salva
          </Button>
        </div>
      )}
    </div>
  );
}
